import React, { useEffect, useState, useRef } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { usePreparePlaylists } from "../../../hooks/usePreparePlaylists";
import { transferToSpotifyWithProgress } from "../../../utils/transferToSpotifyWithProgress";
import ShowProgressModal from "../../../components/ShowProgressModal";
import { formatDuration } from "../../../helper/formatDuration";
import { updateHistoryOnSupabase } from '../../../api/SupabaseApi';
import { useSupabaseUser } from '../../../contexts/SupabaseUserContext';

const SyncSummary = ({
  sourcePlatform,
  selectedPlaylists,
  destinationPlatform,
  onContinue,
  onBack,
}) => {
  const { enhancePlaylistsWithTracks } = usePreparePlaylists();
  const [playlistsWithTracks, setPlaylistsWithTracks] = useState([]);
  const [expandedPlaylistId, setExpandedPlaylistId] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [accessTotalTrack, setAccessTotalTrack] = useState(5);
  const [progress, setProgress] = useState({
    total: 0,
    current: 0,
    success: 0,
    failed: 0,
    currentTrack: null,
    status: "idle",
    errors: [],
  });
  const progressTimer = useRef(null);
  const [overwriteModal, setOverwriteModal] = useState({
    show: false,
    playlist: null,
    onConfirm: null,
    onCancel: null,
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const supabaseUser = useSupabaseUser();
 

  const getTotalTracks = () => {
    return selectedPlaylists.reduce(
      (total, playlist) => total + playlist.trackCount,
      0
    );
  };

  const getTotalDuration = () => {
    const totalSeconds = playlistsWithTracks.reduce(
      (total, playlist) => total + (playlist.duration || 0),
      0
    );
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m${seconds > 0 ? ` ${seconds}s` : ''}`;
    } else {
      return `${seconds}s`;
    }
  };

  const getPlatformIcon = (platform) => {
    return platform === "spotify" ? "Music" : "Play";
  };

  const getPlatformColor = (platform) => {
    return platform === "spotify" ? "text-success" : "text-error";
  };

  const getPlatformBg = (platform) => {
    return platform === "spotify" ? "bg-success/10" : "bg-error/10";
  };

  const enhancePlaylists = async () => {
    const playlistsWithTracks = await enhancePlaylistsWithTracks(
      selectedPlaylists,
      sourcePlatform
    );
    setPlaylistsWithTracks(playlistsWithTracks);
  };

  useEffect(() => {
    enhancePlaylists();
  }, []);

  const handleContinue = async () => {
    const totalTracks = playlistsWithTracks.reduce(
      (sum, pl) => sum + (pl.tracks?.length || 0),
      0
    );
    if (totalTracks > accessTotalTrack) {
      setShowUpgradeModal(true);
      return;
    }
    if (sourcePlatform === "youtube") {
      const getAccessToken = () => localStorage.getItem("accessToken");
      const accessToken = getAccessToken();
      if (!accessToken) throw new Error("Spotify token missing");

      // 1. Build filtered list of playlists to transfer
      let playlistsRes = await fetch(
        "https://api.spotify.com/v1/me/playlists?limit=50",
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      let playlistsData = await playlistsRes.json();

      let playlistsToTransfer = [];
      for (const playlist of playlistsWithTracks) {
        const exists = playlistsData.items.some(
          (pl) => pl.name === playlist.name
        );
        if (exists) {
          const proceed = await new Promise((resolve) => {
            setOverwriteModal({
              show: true,
              playlist: playlist.name,
              onConfirm: () => {
                setOverwriteModal((m) => ({ ...m, show: false }));
                resolve(true);
              },
              onCancel: () => {
                setOverwriteModal((m) => ({ ...m, show: false }));
                resolve(false);
              },
            });
          });
          if (!proceed) continue; // skip this playlist if user cancels
        }
        playlistsToTransfer.push(playlist);
      }

      if (playlistsToTransfer.length === 0) {
        // No playlists to transfer, do not show progress modal
        return;
      }
      setShowProgressModal(true);
      setProgress({
        total: playlistsToTransfer.reduce(
          (sum, pl) => sum + (pl.tracks?.length || 0),
          0
        ),
        current: 0,
        success: 0,
        failed: 0,
        currentTrack: null,
        status: "in-progress",
        errors: [],
      });

      // 3. Transfer only the filtered playlists
      progressTimer.current = setInterval(() => {
        // elapsed removed
      }, 1000);
      try {
        await transferToSpotifyWithProgress(playlistsToTransfer, setProgress);
        setProgress((prev) => ({ ...prev, status: "done" }));
        // After sync is done, update history for each playlist
        for (const playlist of playlistsWithTracks) {
          await updateHistoryOnSupabase({...playlist, sourcePlatform, destinationPlatform}, supabaseUser?.id);
        }
      } catch (err) {
        setProgress((prev) => ({ ...prev, status: "done" }));
      } finally {
        clearInterval(progressTimer.current);
      }
    }
    if (sourcePlatform === "spotify") {
      // Spotify to YouTube transfer logic
      // 1. Fetch user's YouTube playlists
      const getYouTubeToken = () => localStorage.getItem("googleToken");
      const youtubeToken = getYouTubeToken();
      if (!youtubeToken) throw new Error("YouTube token missing");
      let ytPlaylistsRes = await fetch(
        `https://www.googleapis.com/youtube/v3/playlists?part=snippet&mine=true&maxResults=50`,
        {
          headers: { Authorization: `Bearer ${youtubeToken}` },
        }
      );
      let ytPlaylistsData = await ytPlaylistsRes.json();
      let playlistsToTransfer = [];
      for (const playlist of playlistsWithTracks) {
        const exists = ytPlaylistsData.items?.some(
          (pl) => pl.snippet.title === playlist.name
        );
        if (exists) {
          const proceed = await new Promise((resolve) => {
            setOverwriteModal({
              show: true,
              playlist: playlist.name,
              onConfirm: () => {
                setOverwriteModal((m) => ({ ...m, show: false }));
                resolve(true);
              },
              onCancel: () => {
                setOverwriteModal((m) => ({ ...m, show: false }));
                resolve(false);
              },
            });
          });
          if (!proceed) continue; // skip this playlist if user cancels
        }
        playlistsToTransfer.push(playlist);
      }
      if (playlistsToTransfer.length === 0) {
        // No playlists to transfer, do not show progress modal
        return;
      }
      // 2. Show progress modal
      setShowProgressModal(true);
      setProgress({
        total: playlistsToTransfer.reduce(
          (sum, pl) => sum + (pl.tracks?.length || 0),
          0
        ),
        current: 0,
        success: 0,
        failed: 0,
        currentTrack: null,
        status: "in-progress",
        errors: [],
      });

      try {
        // Use the transferSpotifyToYouTube utility
        await import("../../../utils/transferSpotifyToYouTube").then(
          ({ transferSpotifyToYouTube }) =>
            transferSpotifyToYouTube(playlistsToTransfer, setProgress)
        );
        setProgress((prev) => ({ ...prev, status: "done" }));
        // After sync is done, update history for each playlist
        for (const playlist of playlistsWithTracks) {
          await updateHistoryOnSupabase({...playlist, sourcePlatform, destinationPlatform}, supabaseUser?.id);
        }
      } catch (err) {
        setProgress((prev) => ({ ...prev, status: "done" }));
      } finally {
        clearInterval(progressTimer.current);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-text-primary mb-2">
          Sync Summary
        </h2>
        <p className="text-text-secondary">
          Review your selection before proceeding
        </p>
      </div>

      {/* Sync Flow Visualization */}
      <div className="flex items-center justify-center space-x-4 p-6 bg-surface-secondary rounded-xl">
        {/* Source Platform */}
        <div className="text-center">
          <div
            className={`w-16 h-16 rounded-xl ${getPlatformBg(
              sourcePlatform
            )} flex items-center justify-center mb-3`}
          >
            <Icon
              name={getPlatformIcon(sourcePlatform)}
              size={32}
              className={getPlatformColor(sourcePlatform)}
            />
          </div>
          <p className="text-sm font-medium text-text-primary capitalize">
            {sourcePlatform}
          </p>
          <p className="text-xs text-text-muted">Source</p>
        </div>

        {/* Arrow */}
        <div className="flex items-center space-x-2">
          <div className="w-8 h-0.5 bg-primary"></div>
          <Icon name="ArrowRight" size={20} className="text-primary" />
          <div className="w-8 h-0.5 bg-primary"></div>
        </div>

        {/* Destination Platform */}
        <div className="text-center">
          <div
            className={`w-16 h-16 rounded-xl ${getPlatformBg(
              destinationPlatform
            )} flex items-center justify-center mb-3`}
          >
            <Icon
              name={getPlatformIcon(destinationPlatform)}
              size={32}
              className={getPlatformColor(destinationPlatform)}
            />
          </div>
          <p className="text-sm font-medium text-text-primary capitalize">
            {destinationPlatform}
          </p>
          <p className="text-xs text-text-muted">Destination</p>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-surface rounded-lg border border-border">
          <div className="text-2xl font-bold text-primary mb-1">
            {selectedPlaylists.length}
          </div>
          <div className="text-xs text-text-muted">
            Playlist{selectedPlaylists.length !== 1 ? "s" : ""}
          </div>
        </div>

        <div className="text-center p-4 bg-surface rounded-lg border border-border">
          <div className="text-2xl font-bold text-accent mb-1">
            {getTotalTracks()}
          </div>
          <div className="text-xs text-text-muted">Total Tracks</div>
        </div>

        <div className="text-center p-4 bg-surface rounded-lg border border-border">
          <div className="text-2xl font-bold text-secondary mb-1">
            {getTotalDuration()}
          </div>
          <div className="text-xs text-text-muted">Duration</div>
        </div>

        <div className="text-center p-4 bg-surface rounded-lg border border-border">
          <div className="text-2xl font-bold text-warning mb-1">
            ~{Math.ceil(getTotalTracks() / 10)}
          </div>
          <div className="text-xs text-text-muted">Est. Minutes</div>
        </div>
      </div>

      {/* Selected Playlists */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-text-primary">
          Selected Playlists ({selectedPlaylists.length})
        </h3>

        <div className="space-y-3 max-h-64 overflow-y-auto">
          {playlistsWithTracks.map((playlist) => (
            <div
              key={playlist.id}
              className="flex flex-col bg-surface rounded-lg border border-border shadow-sm transition-shadow hover:shadow-lg group"
            >
              <div
                className="flex items-center space-x-3 p-3 cursor-pointer select-none"
                onClick={() =>
                  setExpandedPlaylistId(
                    expandedPlaylistId === playlist.id ? null : playlist.id
                  )
                }
              >
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-surface-secondary flex-shrink-0 border border-border/50">
                  <img
                    src={playlist.coverUrl}
                    alt={playlist.name}
                    className="w-full h-full object-cover transition-transform duration-200"
                    onError={(e) => {
                      e.target.src = "/assets/images/no_image.png";
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-text-primary line-clamp-1">
                    {playlist.name}
                  </h4>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted mt-1">
                    <span className="flex items-center gap-1">
                      <Icon name="Music" size={12} />
                      <span>{playlist.trackCount} tracks</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      <span>
                        {formatDuration(playlist.duration)}
                      </span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon
                        name={playlist.public ? "Globe" : "Lock"}
                        size={12}
                      />
                      <span>{playlist.public ? "Public" : "Private"}</span>
                    </span>
                  </div>
                </div>
                <button
                  className="ml-2 text-text-muted hover:text-primary transition p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/30"
                  tabIndex={0}
                  aria-label={
                    expandedPlaylistId === playlist.id
                      ? "Collapse playlist"
                      : "Expand playlist"
                  }
                >
                  <Icon
                    name={
                      expandedPlaylistId === playlist.id
                        ? "ChevronUp"
                        : "ChevronDown"
                    }
                    size={18}
                  />
                </button>
              </div>

              {/* Accordion content */}
              {expandedPlaylistId === playlist.id && (
                <div className="px-6 pb-3 animate-fade-in">
                  <ul className="space-y-2">
                    {playlist.tracks &&
                      playlist.tracks.map((track, idx) => (
                        <li
                          key={track.videoId || track.id || idx}
                          className="flex items-center gap-3 text-sm bg-surface-secondary/60 rounded-lg p-2 hover:bg-surface-secondary/90 transition"
                        >
                          <div className="w-10 h-10 rounded border border-border/30 overflow-hidden flex-shrink-0 bg-surface">
                            <img
                              src={
                                track.thumbnail ||
                                (track.videoId
                                  ? `https://i.ytimg.com/vi/${track.videoId}/default.jpg`
                                  : "/assets/images/no_image.png")
                              }
                              alt={track.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = "/assets/images/no_image.png";
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate text-text-primary">
                              {track.title}
                            </div>
                            {track.artist && (
                              <div className="text-xs text-text-muted truncate m-1">
                                {track.artist}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Important Notes */}
      <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon
            name="AlertTriangle"
            size={20}
            className="text-warning flex-shrink-0 mt-0.5"
          />
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-warning">
              Important Notes
            </h4>
            <ul className="text-xs text-text-secondary space-y-1">
              <li>• Songs will be matched by title, artist, and duration</li>
              <li>
                • Some tracks may not be available on the destination platform
              </li>
              <li>• Playlist metadata (name, description) will be preserved</li>
              <li>• You can review and retry failed matches after sync</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <Button
          variant="ghost"
          onClick={onBack}
          iconName="ArrowLeft"
          iconPosition="left"
          className="sm:w-auto"
        >
          Back to Selection
        </Button>
        {/* 
         <Button
          variant="primary"
          onClick={onContinue}
          iconName="ArrowRight"
          iconPosition="right"
          className="sm:flex-1"
        >
          Continue to Preview
        </Button>  */}

        <Button
          iconPosition="right"
          iconName="ArrowRight"
          onClick={handleContinue}
          variant="primary"
          disabled={playlistsWithTracks.length === 0}
          className="sm:flex-1"
        >
          Start Sync
        </Button>
      </div>

      {/* Progress Modal */}
      <ShowProgressModal
        show={showProgressModal}
        progress={progress}
        onClose={() => setShowProgressModal(false)}
        sourcePlatform={sourcePlatform}
      />

      {/* Overwrite Modal */}
      {overwriteModal.show && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 ">
          <div className="bg-[#0f172b] rounded-xl shadow-2xl p-8 w-full max-w-lg text-center mx-[20px] relative">
            <div className="flex flex-col items-center mb-4">
              <svg
                className="w-14 h-14 text-yellow-400 mb-2 animate-pulse"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#facc15"
                  strokeWidth="2.5"
                  fill="#facc1533"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4m0 4h.01"
                  stroke="#facc15"
                  strokeWidth="2.5"
                />
              </svg>
              <h2 className="text-2xl font-bold text-yellow-400 mb-2">
                Playlist Exists
              </h2>
            </div>
            <p className="mb-2 text-white text-lg">
              A playlist named{" "}
              <span className="font-semibold text-yellow-200">
                {overwriteModal.playlist}
              </span>{" "}
              already exists.
            </p>
            <p className="mb-6 text-white text-base">
              Do you want to overwrite it?
            </p>
            <div className="flex gap-6 justify-center mt-2">
              <Button variant="primary" onClick={overwriteModal.onConfirm}>
                Overwrite
              </Button>
              <Button variant="outline" onClick={overwriteModal.onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-[#0f172b] rounded-xl shadow-2xl p-8 w-full max-w-lg text-center mx-[20px] relative">
            <div className="flex flex-col items-center mb-4">
              <svg
                className="w-14 h-14 text-blue-400 mb-2"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#3b82f6"
                  strokeWidth="2.5"
                  fill="#3b82f633"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4m0 4h.01"
                  stroke="#3b82f6"
                  strokeWidth="2.5"
                />
              </svg>
              <h2 className="text-2xl font-bold text-blue-400 mb-2 animate-pulse">
                Upgrade Required
              </h2>
            </div>
            <p className="mb-2 text-white text-md">
              You can only sync up to <strong className="text-blue-400">{accessTotalTrack}</strong> tracks as per your your plan.
            </p>
            <p className="mb-6 text-white text-base text-[18px] mt-2">
              Upgrade your plan to sync more tracks.
            </p>
            <div className="flex gap-6 justify-center mt-2">
              <Button variant="primary" onClick={() => window.location.href = '/pricing'}>
                View Pricing
              </Button>
              <Button variant="outline" onClick={() => setShowUpgradeModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncSummary;
